import { Box, Text } from '@chakra-ui/react';
import cn from 'classnames';
import { produce } from 'immer';
import { createParser, Options, useQueryState } from 'nuqs';
import React, { useTransition } from 'react';

import { FeatureTypeList } from '@/services/filters/new-filters.service';

import { NewFilter } from '../new-filter/new-filter';
import { isFilterEqual, useResetPage } from '../utils';

import styles from './new-features.module.scss';

export const NewFeatures: React.FC<{
  count: number;
  featureCounts: FeatureTypeList;
}> = ({ count, featureCounts }) => {

  const mappingFunction = <T extends { id: number }>(
    item: T,
  ): T & { isChecked: boolean } => {
    return {
      isChecked: false,
      ...item,
    };
  };

  const createMapParser = <T extends { id: number; isChecked: boolean }>(
    items: T[],
  ) =>
    createParser({
      parse(queryValue) {
        return items.map((item) => {
          item.isChecked = queryValue.includes(item.id.toString());
          return item;
        });
      },
      serialize(value) {
        const selected = value
          .filter((item) => item.isChecked)
          .map((item) => item.id);

        return selected.length ? selected.join(',') : '';
      },
      eq(a, b) {
        return isFilterEqual(a, b);
      },
    });

  const [_, startTransition] = useTransition();

  const resetPage = useResetPage();

  const mappedFeatures = featureCounts.map(mappingFunction);
  const parseFeatures = createMapParser(mappedFeatures);
  const [queryFeatures, setQueryFeatures] = useQueryState(
    'features',
    parseFeatures
      .withDefault(mappedFeatures)
      .withOptions({ startTransition, shallow: false }),
  );


  const toggleFilter = async (
    id: number,
    setter: (
      value: any[] | ((old: any[]) => any[] | null) | null,
      options?: Options,
    ) => Promise<URLSearchParams>,
  ) => {
    // Reset page to 1 when feature filters change
    resetPage();

    await setter((options) =>
      produce(options, (draft) => {
        draft?.forEach((opt) => {
          if (opt.id === id) {
            opt.isChecked = !opt.isChecked;
          }
        });
      }),
    );
  };

  const toTitleCase = (value: string) =>
    value
      .toLowerCase()
      .split(' ')
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join(' ');

  return (
    <NewFilter label="Features" placement="bottom-end" count={count}>
      <Box className={styles.featuresWrapper}>
        <Box className={styles.featuresList}>
          {[...queryFeatures]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((feature) => {
            return (
              <Box
                key={feature.id}
                onClick={() => {
                  if (feature && feature?.count > 0) {
                    toggleFilter(feature.id, setQueryFeatures);
                  }
                }}
                className={cn(
                  styles.featureItem,
                  feature.isChecked && styles.featureSelected,
                  featureCounts.find((item) => feature.id === item.id)
                    ?.count === 0 && styles.featureDisabled,
                )}
              >
                <Text className={styles.featureText}>
                  {toTitleCase(feature.name)}
                  <Text as="span" className={styles.filterCount}>
                    {` (${feature?.count})`}
                  </Text>
                </Text>
              </Box>
            );
          })}
        </Box>
      </Box>
    </NewFilter>
  );
};
